import { defineStore } from 'pinia'
import { useRuntimeConfig } from '#imports'
import type {
    ClientInquiryResponse,
    DeepResearchEventType,
    DeepResearchReference,
    DeepResearchState,
    DeepResearchStatus,
    DeepResearchStep,
    DeepResearchTimelineEntry,
} from '../utils/type'

const FOLLOW_UP_EVENT_ALIASES = new Map<string, DeepResearchEventType>([
    ['followup', 'follow_up'],
    ['follow-up', 'follow_up'],
    ['follow_up_question', 'follow_up'],
    ['follow_up_questions', 'follow_up'],
])

const START_EVENT_TYPES = new Set<string>(['start', 'started', 'open', 'begin'])
const COMPLETE_EVENT_TYPES = new Set<string>(['complete', 'completed', 'done', 'close', 'end'])
const SKIPPED_EVENT_TYPES = new Set<string>(['ping', 'keepalive', 'keep-alive', 'heartbeat'])

function createInitialDeepResearchState(): DeepResearchState {
    return {
        status: 'idle',
        currentStep: null,
        totalSteps: null,
        steps: [],
        summary: null,
        insights: [],
        references: [],
        followUpQuestions: [],
        timeline: [],
        error: null,
        sessionId: undefined,
        historyId: undefined,
        inquiryId: undefined,
        lastUpdatedAt: null,
    }
}

function normalizeEventType(eventType: string): string {
    if (!eventType) {
        return 'message'
    }
    const normalized = eventType.trim().toLowerCase()
    return FOLLOW_UP_EVENT_ALIASES.get(normalized) ?? normalized
}

function parseStreamPayload(rawData: string): unknown {
    const trimmed = rawData?.trim()
    if (!trimmed) {
        return ''
    }

    try {
        return JSON.parse(trimmed)
    } catch (error) {
        return trimmed
    }
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function mapStatusString(value: string): DeepResearchStatus | null {
    const normalized = value.toLowerCase()

    if (normalized.includes('fail') || normalized.includes('error')) {
        return 'failed'
    }
    if (normalized.includes('queue')) {
        return 'queued'
    }
    if (normalized.includes('synth')) {
        return 'synthesizing'
    }
    if (
        normalized.includes('complete') ||
        normalized.includes('done') ||
        normalized.includes('finish') ||
        normalized.includes('close')
    ) {
        return 'completed'
    }
    if (
        normalized.includes('run') ||
        normalized.includes('progress') ||
        normalized.includes('work') ||
        normalized.includes('research')
    ) {
        return 'running'
    }

    return null
}

function normalizeFollowUpPayload(payload: unknown): string[] {
    if (!payload) {
        return []
    }

    const uniqueQuestions = new Set<string>()

    const addQuestion = (question: unknown) => {
        if (typeof question === 'string') {
            const trimmed = question.trim()
            if (trimmed.length > 0) {
                uniqueQuestions.add(trimmed)
            }
        }
    }

    if (typeof payload === 'string') {
        addQuestion(payload)
        return Array.from(uniqueQuestions)
    }

    if (Array.isArray(payload)) {
        payload.forEach(addQuestion)
        return Array.from(uniqueQuestions)
    }

    if (isRecord(payload)) {
        Object.entries(payload).forEach(([key, value]) => {
            if (key === 'question' || key === 'follow_up_question') {
                addQuestion(value)
            }

            if (
                key === 'questions' ||
                key === 'followUpQuestions' ||
                key === 'follow_up_questions'
            ) {
                if (Array.isArray(value)) {
                    value.forEach(addQuestion)
                }
            }
        })

        return Array.from(uniqueQuestions)
    }

    return []
}

function extractSummary(payload: unknown): string | null {
    if (typeof payload === 'string') {
        return payload.trim()
    }

    if (isRecord(payload)) {
        const summaryLikeFields = ['summary', 'text', 'content', 'message']
        for (const key of summaryLikeFields) {
            if (typeof payload[key] === 'string') {
                const text = (payload[key] as string).trim()
                if (text.length > 0) {
                    return text
                }
            }
        }
    }

    return null
}

function extractInsight(payload: unknown): string | null {
    if (typeof payload === 'string') {
        return payload.trim()
    }

    if (isRecord(payload)) {
        const insightLikeFields = ['insight', 'observation', 'finding', 'text', 'content']
        for (const key of insightLikeFields) {
            if (typeof payload[key] === 'string') {
                const text = (payload[key] as string).trim()
                if (text.length > 0) {
                    return text
                }
            }
        }
    }

    return null
}

function extractReferences(payload: unknown): DeepResearchReference[] {
    const references: DeepResearchReference[] = []

    const createReference = (value: unknown, index: number): DeepResearchReference | null => {
        if (typeof value === 'string') {
            const trimmed = value.trim()
            if (trimmed.length === 0) {
                return null
            }
            return {
                id: `ref-${index}`,
                title: trimmed,
                snippet: trimmed,
            }
        }

        if (isRecord(value)) {
            const id =
                typeof value.id === 'string'
                    ? value.id
                    : typeof value.identifier === 'string'
                      ? value.identifier
                      : `ref-${index}`

            const title =
                typeof value.title === 'string'
                    ? value.title
                    : typeof value.name === 'string'
                      ? value.name
                      : typeof value.url === 'string'
                        ? value.url
                        : `Reference ${index + 1}`

            const snippet = typeof value.snippet === 'string' ? value.snippet : undefined
            const url = typeof value.url === 'string' ? value.url : undefined

            const metadata: Record<string, unknown> = { ...value }

            return {
                id,
                title,
                url,
                snippet,
                metadata,
            }
        }

        return null
    }

    if (Array.isArray(payload)) {
        payload.forEach((value, index) => {
            const reference = createReference(value, index)
            if (reference) {
                references.push(reference)
            }
        })
        return references
    }

    if (isRecord(payload)) {
        if (Array.isArray(payload.references)) {
            return extractReferences(payload.references)
        }

        const reference = createReference(payload, 0)
        if (reference) {
            references.push(reference)
        }
    }

    return references
}

function toTimelineEntry(
    eventType: string,
    payload: unknown,
    timestamp: string,
): DeepResearchTimelineEntry {
    let text = ''

    if (typeof payload === 'string') {
        text = payload
    } else if (isRecord(payload)) {
        if (typeof payload.message === 'string') {
            text = payload.message
        } else if (typeof payload.text === 'string') {
            text = payload.text
        }
    }

    if (!text) {
        text = eventType
    }

    const entry: DeepResearchTimelineEntry = {
        type: eventType,
        text,
        timestamp,
    }

    if (isRecord(payload)) {
        entry.metadata = payload
    }

    return entry
}

function updateStepCollection(steps: DeepResearchStep[], payload: Record<string, unknown>) {
    const stepIdentifier =
        payload.stepId ?? payload.step_id ?? payload.stepID ?? payload.id ?? payload.step ?? payload.index

    const stepId =
        typeof stepIdentifier === 'string'
            ? stepIdentifier
            : typeof stepIdentifier === 'number'
              ? String(stepIdentifier)
              : undefined

    if (!stepId) {
        return
    }

    const existingIndex = steps.findIndex((step) => step.id === stepId)
    const existingStep = existingIndex >= 0 ? steps[existingIndex] : undefined

    const title =
        typeof payload.title === 'string'
            ? payload.title
            : typeof payload.name === 'string'
              ? payload.name
              : existingStep?.title ?? `Step ${stepId}`

    const content =
        typeof payload.content === 'string'
            ? payload.content
            : typeof payload.text === 'string'
              ? payload.text
              : typeof payload.description === 'string'
                ? payload.description
                : existingStep?.content ?? ''

    const completedRaw = payload.completed ?? payload.isCompleted ?? payload.done ?? payload.finished
    const completed =
        typeof completedRaw === 'boolean'
            ? completedRaw
            : typeof completedRaw === 'string'
              ? completedRaw.toLowerCase() === 'true'
              : Boolean(completedRaw)

    const startedAtValue =
        typeof payload.startedAt === 'string'
            ? payload.startedAt
            : typeof payload.started_at === 'string'
              ? payload.started_at
              : undefined

    const completedAtValue =
        typeof payload.completedAt === 'string'
            ? payload.completedAt
            : typeof payload.completed_at === 'string'
              ? payload.completed_at
              : undefined

    const metadata = existingStep?.metadata ? { ...existingStep.metadata } : {}
    Object.assign(metadata, payload)

    const resolvedStartedAt =
        startedAtValue ?? existingStep?.startedAt ?? (completed ? new Date().toISOString() : null)

    const resolvedCompletedAt = completed
        ? completedAtValue ?? existingStep?.completedAt ?? new Date().toISOString()
        : completedAtValue ?? existingStep?.completedAt ?? null

    const updatedStep: DeepResearchStep = {
        id: stepId,
        title,
        content,
        completed,
        startedAt: resolvedStartedAt,
        completedAt: resolvedCompletedAt,
        metadata,
    }

    if (existingIndex === -1) {
        steps.push(updatedStep)
    } else {
        steps.splice(existingIndex, 1, {
            ...existingStep,
            ...updatedStep,
        })
    }
}

export const useDeepResearchStore = defineStore('deepResearch', {
    state: () => ({
        clientInquiry: null as ClientInquiryResponse | null,
        researchState: createInitialDeepResearchState(),
        streaming: false,
        lastEventType: null as string | null,
        error: null as string | null,
    }),
    getters: {
        hasClientInquiry: (state) => state.clientInquiry !== null,
        hasFollowUp: (state) => state.researchState.followUpQuestions.length > 0,
        nextFollowUpQuestion: (state) => state.researchState.followUpQuestions[0] ?? null,
        followUpNotification: (state) => ({
            hasFollowUps: state.researchState.followUpQuestions.length > 0,
            next: state.researchState.followUpQuestions[0] ?? null,
            count: state.researchState.followUpQuestions.length,
        }),
        timeline: (state) => state.researchState.timeline,
        isStreaming: (state) => state.streaming,
    },
    actions: {
        reset() {
            this.clientInquiry = null
            this.researchState = createInitialDeepResearchState()
            this.streaming = false
            this.lastEventType = null
            this.error = null
        },
        syncSession(sessionId: string | null | undefined) {
            if (!sessionId) {
                return
            }

            this.researchState.sessionId = sessionId
            if (this.clientInquiry) {
                this.clientInquiry = {
                    ...this.clientInquiry,
                    sessionId,
                }
            }
        },
        syncHistoryId(historyId: string | null | undefined) {
            if (!historyId) {
                return
            }

            this.researchState.historyId = historyId
            if (this.clientInquiry) {
                this.clientInquiry = {
                    ...this.clientInquiry,
                    historyId,
                }
            }
        },
        syncInquiryId(inquiryId: string | null | undefined) {
            if (!inquiryId) {
                return
            }

            this.researchState.inquiryId = inquiryId
            if (this.clientInquiry) {
                this.clientInquiry = {
                    ...this.clientInquiry,
                    inquiryId,
                }
            }
        },
        setClientInquiry(payload: Partial<ClientInquiryResponse>) {
            const base: ClientInquiryResponse = {
                sessionId:
                    payload.sessionId ?? this.researchState.sessionId ?? this.clientInquiry?.sessionId ?? '',
                historyId:
                    payload.historyId ?? this.researchState.historyId ?? this.clientInquiry?.historyId ?? '',
                inquiryId:
                    payload.inquiryId ?? this.researchState.inquiryId ?? this.clientInquiry?.inquiryId ?? '',
                inquiry: payload.inquiry ?? this.clientInquiry?.inquiry ?? '',
                answer: payload.answer ?? this.clientInquiry?.answer,
                createdAt:
                    payload.createdAt ?? this.clientInquiry?.createdAt ?? new Date().toISOString(),
                metadata: {
                    ...(this.clientInquiry?.metadata ?? {}),
                    ...(payload.metadata ?? {}),
                },
            }

            this.clientInquiry = base
            this.syncSession(base.sessionId)
            this.syncHistoryId(base.historyId)
            this.syncInquiryId(base.inquiryId)
        },
        enqueueFollowUp(question: string) {
            if (!question) {
                return
            }
            const trimmed = question.trim()
            if (!trimmed) {
                return
            }

            if (!this.researchState.followUpQuestions.includes(trimmed)) {
                this.researchState.followUpQuestions.push(trimmed)
            }
        },
        enqueueFollowUps(questions: string[]) {
            questions.forEach((question) => this.enqueueFollowUp(question))
        },
        dequeueFollowUp() {
            if (this.researchState.followUpQuestions.length === 0) {
                return null
            }

            return this.researchState.followUpQuestions.shift() ?? null
        },
        clearFollowUps() {
            this.researchState.followUpQuestions = []
        },
        setStreaming(value: boolean) {
            this.streaming = value
        },
        setError(message: string | null) {
            this.error = message
            this.researchState.error = message
            if (message) {
                this.researchState.status = 'failed'
            }
        },
        ingestStreamChunk(chunk: string) {
            if (!chunk) {
                return
            }

            const segments = chunk
                .split(/\n\n/)
                .map((segment) => segment.trim())
                .filter((segment) => segment.length > 0)

            if (segments.length === 0) {
                return
            }

            segments.forEach((segment) => {
                let eventType = 'message'
                const dataLines: string[] = []
                const lines = segment.split(/\n/)

                lines.forEach((line) => {
                    const trimmedLine = line.trim()
                    if (trimmedLine.startsWith('event:')) {
                        eventType = trimmedLine.slice(6).trim()
                    } else if (trimmedLine.startsWith('data:')) {
                        dataLines.push(trimmedLine.slice(5).trim())
                    }
                })

                const rawData = dataLines.length > 0 ? dataLines.join('\n') : segment

                this.applyStreamEvent(eventType, rawData)
            })
        },
        applyStreamEvent(eventType: string, rawData: string) {
            const normalizedType = normalizeEventType(eventType)

            if (SKIPPED_EVENT_TYPES.has(normalizedType)) {
                return
            }

            if (START_EVENT_TYPES.has(normalizedType)) {
                this.researchState.status = 'running'
                this.streaming = true
                return
            }

            if (COMPLETE_EVENT_TYPES.has(normalizedType)) {
                this.researchState.status = 'completed'
                this.streaming = false
                return
            }

            const payload = parseStreamPayload(rawData)
            const timestamp = new Date().toISOString()

            this.researchState.lastUpdatedAt = timestamp
            this.lastEventType = normalizedType

            if (normalizedType !== 'metadata') {
                const entry = toTimelineEntry(normalizedType, payload, timestamp)
                this.researchState.timeline.push(entry)
            }

            if (normalizedType === 'error') {
                const message =
                    typeof payload === 'string'
                        ? payload
                        : isRecord(payload) && typeof payload.message === 'string'
                          ? payload.message
                          : rawData
                this.setError(message)
                return
            }

            if (normalizedType === 'summary') {
                const summary = extractSummary(payload)
                if (summary) {
                    this.researchState.summary = summary
                }
            }

            if (normalizedType === 'insight') {
                const insight = extractInsight(payload)
                if (insight) {
                    this.researchState.insights.push(insight)
                }
            }

            if (normalizedType === 'reference') {
                const references = extractReferences(payload)
                if (references.length > 0) {
                    const existingIds = new Set(this.researchState.references.map((item) => item.id))
                    references.forEach((reference) => {
                        if (!existingIds.has(reference.id)) {
                            this.researchState.references.push(reference)
                        }
                    })
                }
            }

            if (normalizedType === 'follow_up') {
                const followUpQuestions = normalizeFollowUpPayload(payload)
                if (followUpQuestions.length > 0) {
                    this.enqueueFollowUps(followUpQuestions)
                }
            }

            if (normalizedType === 'metadata' && isRecord(payload)) {
                if (typeof payload.sessionId === 'string') {
                    this.syncSession(payload.sessionId)
                }
                if (typeof payload.session_id === 'string') {
                    this.syncSession(payload.session_id)
                }
                if (typeof payload.historyId === 'string') {
                    this.syncHistoryId(payload.historyId)
                }
                if (typeof payload.history_id === 'string') {
                    this.syncHistoryId(payload.history_id)
                }
                if (typeof payload.inquiryId === 'string') {
                    this.syncInquiryId(payload.inquiryId)
                }
                if (typeof payload.inquiry_id === 'string') {
                    this.syncInquiryId(payload.inquiry_id)
                }
                if (typeof payload.status === 'string') {
                    const status = mapStatusString(payload.status)
                    if (status) {
                        this.researchState.status = status
                    }
                }
                if (typeof payload.state === 'string') {
                    const status = mapStatusString(payload.state)
                    if (status) {
                        this.researchState.status = status
                    }
                }
                if (typeof payload.currentStep === 'number') {
                    this.researchState.currentStep = payload.currentStep
                }
                if (typeof payload.current_step === 'number') {
                    this.researchState.currentStep = payload.current_step
                }
                if (typeof payload.totalSteps === 'number') {
                    this.researchState.totalSteps = payload.totalSteps
                }
                if (typeof payload.total_steps === 'number') {
                    this.researchState.totalSteps = payload.total_steps
                }
                return
            }

            if (isRecord(payload)) {
                if (typeof payload.status === 'string') {
                    const status = mapStatusString(payload.status)
                    if (status) {
                        this.researchState.status = status
                    }
                }

                if (typeof payload.state === 'string') {
                    const status = mapStatusString(payload.state)
                    if (status) {
                        this.researchState.status = status
                    }
                }

                if (typeof payload.currentStep === 'number') {
                    this.researchState.currentStep = payload.currentStep
                } else if (typeof payload.step === 'number') {
                    this.researchState.currentStep = payload.step
                } else if (typeof payload.index === 'number') {
                    this.researchState.currentStep = payload.index
                } else if (typeof payload.stepIndex === 'number') {
                    this.researchState.currentStep = payload.stepIndex
                }

                if (typeof payload.totalSteps === 'number') {
                    this.researchState.totalSteps = payload.totalSteps
                } else if (typeof payload.total_steps === 'number') {
                    this.researchState.totalSteps = payload.total_steps
                } else if (typeof payload.steps === 'number') {
                    this.researchState.totalSteps = payload.steps
                }

                if (payload.follow_up_questions) {
                    const followUpQuestions = normalizeFollowUpPayload(payload.follow_up_questions)
                    if (followUpQuestions.length > 0) {
                        this.enqueueFollowUps(followUpQuestions)
                    }
                }

                if (payload.followUps) {
                    const followUpQuestions = normalizeFollowUpPayload(payload.followUps)
                    if (followUpQuestions.length > 0) {
                        this.enqueueFollowUps(followUpQuestions)
                    }
                }

                if (payload.questions) {
                    const followUpQuestions = normalizeFollowUpPayload(payload.questions)
                    if (followUpQuestions.length > 0) {
                        this.enqueueFollowUps(followUpQuestions)
                    }
                }

                if (payload.insights) {
                    const insightsSource = Array.isArray(payload.insights)
                        ? payload.insights
                        : [payload.insights]
                    insightsSource.forEach((entry) => {
                        const insight = extractInsight(entry)
                        if (insight && !this.researchState.insights.includes(insight)) {
                            this.researchState.insights.push(insight)
                        }
                    })
                }

                if (Array.isArray(payload.references)) {
                    const references = extractReferences(payload.references)
                    const existingIds = new Set(this.researchState.references.map((item) => item.id))
                    references.forEach((reference) => {
                        if (!existingIds.has(reference.id)) {
                            this.researchState.references.push(reference)
                        }
                    })
                }

                if (payload.summary) {
                    const summary = extractSummary(payload.summary)
                    if (summary) {
                        this.researchState.summary = summary
                    }
                }

                if (payload.error) {
                    if (typeof payload.error === 'string') {
                        this.setError(payload.error)
                    } else if (isRecord(payload.error) && typeof payload.error.message === 'string') {
                        this.setError(payload.error.message)
                    }
                }

                if (
                    typeof payload.sessionId === 'string' ||
                    typeof payload.session_id === 'string'
                ) {
                    this.syncSession(
                        (payload.sessionId as string | undefined) ??
                            (payload.session_id as string | undefined),
                    )
                }

                if (
                    typeof payload.historyId === 'string' ||
                    typeof payload.history_id === 'string'
                ) {
                    this.syncHistoryId(
                        (payload.historyId as string | undefined) ??
                            (payload.history_id as string | undefined),
                    )
                }

                if (
                    typeof payload.inquiryId === 'string' ||
                    typeof payload.inquiry_id === 'string'
                ) {
                    this.syncInquiryId(
                        (payload.inquiryId as string | undefined) ??
                            (payload.inquiry_id as string | undefined),
                    )
                }

                if (
                    typeof payload.clientInquiry === 'string' ||
                    typeof payload.question === 'string' ||
                    typeof payload.inquiry === 'string'
                ) {
                    this.setClientInquiry({
                        inquiry:
                            (payload.clientInquiry as string | undefined) ??
                            (payload.question as string | undefined) ??
                            (payload.inquiry as string | undefined),
                        answer: typeof payload.answer === 'string' ? payload.answer : undefined,
                        metadata: payload,
                    })
                }

                if (
                    typeof payload.client_inquiry === 'string' ||
                    typeof payload.prompt === 'string'
                ) {
                    this.setClientInquiry({
                        inquiry:
                            (payload.client_inquiry as string | undefined) ??
                            (payload.prompt as string | undefined),
                        metadata: payload,
                    })
                }

                updateStepCollection(this.researchState.steps, payload)
            }
        },
        async streamFromApi(
            endpoint: string,
            payload: Record<string, unknown> = {},
            requestInit: RequestInit = {},
        ) {
            try {
                this.setError(null)
                this.setStreaming(true)
                this.researchState.status = 'running'

                const config = useRuntimeConfig()
                const baseUrl =
                    typeof config?.public?.deepResearchStreamApi === 'string'
                        ? config.public.deepResearchStreamApi
                        : ''

                const targetUrl = endpoint.startsWith('http') ? endpoint : `${baseUrl}${endpoint}`

                const headers = new Headers(requestInit.headers ?? {})
                if (!headers.has('Accept')) {
                    headers.set('Accept', 'text/event-stream')
                }
                if (!headers.has('Content-Type')) {
                    headers.set('Content-Type', 'application/json')
                }

                const method = (requestInit.method ?? 'POST').toUpperCase()
                const body =
                    requestInit.body ??
                    (method === 'GET' || method === 'HEAD'
                        ? undefined
                        : JSON.stringify(payload))

                const response = await fetch(targetUrl, {
                    ...requestInit,
                    method,
                    headers,
                    body,
                })

                const reader = response.body?.pipeThrough(new TextDecoderStream()).getReader()
                if (!reader) {
                    throw new Error('SSE stream is not supported by this environment.')
                }

                while (true) {
                    const { value, done } = await reader.read()
                    if (done) {
                        break
                    }

                    if (value) {
                        this.ingestStreamChunk(value)
                    }
                }

                this.streaming = false
                if (this.researchState.status === 'running') {
                    this.researchState.status = 'completed'
                }
            } catch (error) {
                const message = error instanceof Error ? error.message : String(error)
                this.setError(message)
                this.streaming = false
                throw error
            }
        },
    },
})
